import React from "react";
import { Button, Divider, Grid, Header, Item, Label, Segment, Statistic } from 'semantic-ui-react';
import { Profile } from "../../app/models/Profile";
import { observer } from "mobx-react-lite";
import FollowButton from "./FollowButton";
import FriendRequestButton from "../friends/FriendRequestButton";
import { useTranslation } from 'react-i18next';
import { sportOptions } from "../../app/common/options/sportOptions";
import MessageButton from "../messages/MessageButton";
import UserScoreButton from "../userRatings/UserScoreButton";
import { InfluenceWidget } from "../playground/InfluenceWidget";
import { useStore } from "../../app/stores/store";

interface Props {
  profile: Profile;
}

export default observer(function ProfileHeader({ profile }: Props) {
  const { t } = useTranslation();
  const { userStore } = useStore();
  const isOwnProfile = userStore.user?.userID === profile.userID;

  // Get localized sport names
  const getLocalizedSportName = (sportId: string) => {
    const sport = sportOptions.find(opt => opt.value === sportId);
    if (sport) {
      return t(`sports.${sport.label.toLowerCase()}`) || sport.label;
    }
    return null;
  };

  return (
    <Segment>
      <Grid stackable>
        <Grid.Column width={12} mobile={16} tablet={12} computer={12}>
          <Item.Group>
            <Item>
              <Item.Image avatar size='small' src={profile.image || '/assets/user.png'} />
              <Item.Content verticalAlign='middle'>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  flexWrap: 'wrap',
                  gap: '10px',
                  marginBottom: '8px'
                }}>
                  <Header as='h1' content={profile.fullName} style={{ margin: 0 }} />
                  <UserScoreButton
                    userId={profile.userID}
                    buttonText={t('gameDetailedSidebar.viewStats') || "View Stats"}
                    size="medium"
                  />
                </div>
                
                {/* Favorite Sports Section */}
                {profile.sports && profile.sports.length > 0 && (
                  <div style={{ marginTop: '12px' }}>
                    <Header 
                      as='h4' 
                      style={{ 
                        marginBottom: '8px', 
                        color: '#00b5ad',
                        fontWeight: '600'
                      }}
                    >
                      {t('profileHeader.favoriteSports')}
                    </Header>
                    <Label.Group>
                      {profile.sports.map((sport) => {
                        const localizedName = getLocalizedSportName(sport.id);
                        return localizedName ? (
                          <Label 
                            key={sport.id} 
                            color='teal' 
                            size='medium'
                            style={{ marginBottom: '5px' }}
                          >
                            {localizedName}
                          </Label>
                        ) : null;
                      })}
                    </Label.Group>
                  </div>
                )}

              </Item.Content>
            </Item>
          </Item.Group>
        </Grid.Column>
        <Grid.Column width={4} mobile={16} tablet={4} computer={4}>
          <Statistic.Group widths={2} size='small'>
            <Statistic label={t('profileHeader.followers')} value={profile.followersCount} />
            <Statistic label={t('profileHeader.following')} value={profile.followingCount} />
          </Statistic.Group>
          <Statistic.Group widths={1} size='small' style={{ marginTop: '0.5em' }}>
            <Statistic label={t('profileContent.friends')} value={profile.friendsCount || 0} />
          </Statistic.Group>
          <Divider />
          <InfluenceWidget userId={profile.userID} isOwnProfile={isOwnProfile} />
          <Divider />
          <div style={{ marginBottom: '0.8em', position: 'relative', zIndex: 1 }}>
            <FollowButton profile={profile} />
          </div>
          <div style={{ marginBottom: '0.8em', position: 'relative', zIndex: 1 }}>
            <FriendRequestButton userId={profile.userID} />
          </div>
        </Grid.Column>
      </Grid>
    </Segment>
  );
});